import { Injectable, inject } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS } from '@core/interceptors/http-error.interceptor';
import { ApiService } from './api.service';
import {
  AlarmConfigCollectionModel,
  AlarmConfigResponseModel,
  AlarmConfigUpdateModel,
  DeviceAlarmSettingsCollectionModel,
  DeviceOverrideCollectionModel,
  DeviceOverrideResponseModel,
  DeviceOverrideUpdateModel,
} from '@core/models';

@Injectable({
  providedIn: 'root',
})
export class AlarmConfigService {
  private readonly apiService = inject(ApiService);

  getAlarmConfig(): Observable<AlarmConfigCollectionModel> {
    return this.apiService.get(`/alarm/alarm-config`);
  }

  updateAlarmConfig(
    configGroup: string,
    update: AlarmConfigUpdateModel,
  ): Observable<AlarmConfigResponseModel> {
    return this.apiService.put(`/alarm/alarm-config/${configGroup}`, update);
  }

  getDeviceOverrides(configGroup: string): Observable<DeviceOverrideCollectionModel> {
    return this.apiService.get(`/alarm/alarm-config/${configGroup}/device-overrides`);
  }

  updateDeviceOverride(
    configGroup: string,
    deviceId: number,
    update: DeviceOverrideUpdateModel,
  ): Observable<DeviceOverrideResponseModel> {
    return this.apiService.put(
      `/alarm/alarm-config/${configGroup}/device-overrides/${deviceId}`,
      update,
    );
  }

  /** Every config group's settings for one device, in a single request — the
   * device-settings tab's counterpart to calling `getDeviceOverrides` once per config
   * group and filtering client-side for this one device out of the full per-partner device list.
   * Answers 403 to anyone without manage_device_settings for this device, Xemex included; the
   * settings tab shows that as a note of its own, so the global snackbar stays out of it. */
  getDeviceAlarmSettings(deviceId: number): Observable<DeviceAlarmSettingsCollectionModel> {
    return this.apiService.getRaw(
      `/alarm/alarm-config/device-overrides/${deviceId}`,
      {},
      new HttpContext().set(SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS, [403]),
    );
  }
}
